/**
 * Parse a time string (HH:MM or "H:MM AM/PM") to minutes since midnight
 * @param {string} timeStr - Time string
 * @returns {number} Minutes since midnight, or Infinity on failure
 */
export const parseTimeToMinutes = (timeStr) => {
  if (!timeStr || !timeStr.includes(':')) return Infinity;
  try {
    let hours, minutes;
    if (timeStr.includes('AM') || timeStr.includes('PM')) {
      const [timePart, period] = timeStr.split(' ');
      [hours, minutes] = timePart.split(':').map(Number);
      if (period === 'PM' && hours < 12) hours += 12;
      if (period === 'AM' && hours === 12) hours = 0;
    } else {
      [hours, minutes] = timeStr.split(':').map(Number);
    }
    if (isNaN(hours) || isNaN(minutes)) return Infinity;
    return hours * 60 + minutes;
  } catch {
    return Infinity;
  }
};

/**
 * Extract hours and minutes from a Date object in a specific IANA timezone.
 * Falls back to local machine time when no timezone is provided.
 *
 * @param {Date}   date     - The Date object to extract time from
 * @param {string} timezone - IANA timezone string (e.g. "Australia/Sydney")
 * @returns {number} Minutes since midnight in the given timezone
 */
const getMinutesInTimezone = (date, timezone) => {
  if (!date) return 0;

  if (timezone) {
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hour: 'numeric',
        minute: 'numeric',
        hour12: false
      });
      const parts = formatter.formatToParts(date);
      const hour = parseInt(parts.find(p => p.type === 'hour')?.value, 10);
      const minute = parseInt(parts.find(p => p.type === 'minute')?.value, 10);
      if (!isNaN(hour) && !isNaN(minute)) {
        return hour * 60 + minute;
      }
    } catch {
      // Fall through to machine-local time
    }
  }

  return date.getHours() * 60 + date.getMinutes();
};

/**
 * Calculates the position of a prayer marker as a percentage along the progress bar.
 *
 * Day bar spans:   sunrise → sunset   (left=sunrise, right=sunset)
 *   - Dhuhr, Asr:  positioned proportionally between sunrise & sunset
 *   - Maghrib:     positioned at/near the right edge (sunset = end of day)
 *
 * Night bar spans: sunset → sunrise   (left=sunset, right=sunrise)
 *   - Isha:        positioned near the left edge (shortly after sunset)
 *   - Fajr:        positioned near the right edge (shortly before sunrise)
 *
 * @param {string}  timeStr   - Prayer time in HH:MM (location timezone, from Al Adhan API)
 * @param {Date}    sunrise   - Sunrise Date object (UTC-based from sunrise-sunset API)
 * @param {Date}    sunset    - Sunset Date object (UTC-based from sunrise-sunset API)
 * @param {boolean} isDay     - Whether the progress bar is currently showing the day span
 * @param {string}  prayerKey - The prayer name key (fajr, dhuhr, asr, maghrib, isha)
 * @param {string}  timezone  - IANA timezone (e.g. "Australia/Sydney") to interpret sunrise/sunset
 * @returns {number|null}     - Position 0‑1, or null if calculation failed
 */
export const calculatePrayerTimePercentage = (timeStr, sunrise, sunset, isDay, prayerKey, timezone) => {
  if (!timeStr || !sunrise || !sunset) return null;

  const prayerMinutes = parseTimeToMinutes(timeStr);
  if (prayerMinutes === Infinity) return null;

  // Extract sunrise/sunset as minutes-since-midnight in the LOCATION's timezone,
  // not the machine's timezone.  This is critical when the Pi/browser runs in a
  // different timezone than the location being viewed.
  const sunriseMin = getMinutesInTimezone(sunrise, timezone);
  const sunsetMin = getMinutesInTimezone(sunset, timezone);

  const prayer = (prayerKey || '').toLowerCase();

  // ── DAY BAR (sunrise → sunset) ─────────────────────────
  if (isDay) {
    const dayLength = sunsetMin - sunriseMin;
    if (dayLength <= 0) return 0.5;

    // Maghrib = sunset prayer, clamp to right edge
    if (prayer === 'maghrib') {
      return Math.max(0.92, Math.min(1, (prayerMinutes - sunriseMin) / dayLength));
    }

    // Dhuhr, Asr — normal proportional position
    const pct = (prayerMinutes - sunriseMin) / dayLength;
    return Math.max(0, Math.min(1, pct));
  }

  // ── NIGHT BAR (sunset → sunrise) ──────────────────────
  // Night wraps around midnight.  We treat the timeline as:
  //   0% = sunset,  100% = next sunrise
  // Total night length = (minutes from sunset to midnight) + (minutes from midnight to sunrise)
  const minutesSunsetToMidnight = 24 * 60 - sunsetMin;   // sunset → 00:00
  const nightLength = minutesSunsetToMidnight + sunriseMin; // sunset → sunrise (next day)

  if (nightLength <= 0) return 0.5;

  // Convert prayer time to "minutes since sunset"
  let minutesSinceSunset;
  if (prayerMinutes >= sunsetMin) {
    // Evening side (after sunset, before midnight): e.g. Isha at 20:00 when sunset is 18:30
    minutesSinceSunset = prayerMinutes - sunsetMin;
  } else {
    // Morning side (after midnight, before sunrise): e.g. Fajr at 05:00
    minutesSinceSunset = minutesSunsetToMidnight + prayerMinutes;
  }

  const pct = minutesSinceSunset / nightLength;
  return Math.max(0, Math.min(1, pct));
};

/**
 * Gets a one-letter abbreviation for a prayer name
 * @param {string} prayer - Prayer name
 * @returns {string} Single letter abbreviation
 */
export const getPrayerAbbreviation = (prayer) => {
  switch (prayer.toLowerCase()) {
    case 'fajr': return 'F';
    case 'dhuhr': return 'D';
    case 'asr': return 'A';
    case 'maghrib': return 'M';
    case 'isha': return 'I';
    default: return prayer.charAt(0).toUpperCase();
  }
};

/**
 * Find the next upcoming prayer given current time
 * @param {Object} prayerTimes - Object with fajr, dhuhr, asr, maghrib, isha time strings
 * @param {Date} currentTime - Current time at the location
 * @returns {{ prayer: Object, minutesUntil: number, formatted: string } | null}
 */
export const findNextPrayer = (prayerTimes, currentTime) => {
  if (!prayerTimes) return null;

  const currentMinutes = currentTime.getHours() * 60 + currentTime.getMinutes();

  const prayers = [
    { name: 'Fajr', time: prayerTimes.fajr },
    { name: 'Dhuhr', time: prayerTimes.dhuhr },
    { name: 'Asr', time: prayerTimes.asr },
    { name: 'Maghrib', time: prayerTimes.maghrib },
    { name: 'Isha', time: prayerTimes.isha }
  ].map(p => ({ ...p, minutes: parseTimeToMinutes(p.time) }))
   .filter(p => p.minutes !== Infinity);

  if (prayers.length === 0) return null;

  // Find first prayer after current time
  let next = prayers.find(p => p.minutes > currentMinutes);

  // If all passed, next is tomorrow's Fajr
  if (!next) {
    next = { ...prayers[0], minutes: prayers[0].minutes + 24 * 60, isTomorrow: true };
  }

  const minutesUntil = next.minutes - currentMinutes;
  if (isNaN(minutesUntil) || !isFinite(minutesUntil) || minutesUntil < 0) {
    return { prayer: next, minutesUntil: 0, formatted: 'soon' };
  }

  const hoursUntil = Math.floor(minutesUntil / 60);
  const remainingMinutes = Math.floor(minutesUntil % 60);

  return {
    prayer: next,
    minutesUntil,
    formatted: `${hoursUntil}h ${remainingMinutes}m`
  };
};
